import React, { createContext, useEffect, useState } from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  GlobalStyles,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard'
import InsightsIcon from '@mui/icons-material/Insights'
import CampaignIcon from '@mui/icons-material/Campaign'
import BusinessIcon from '@mui/icons-material/Business'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import ScienceIcon from '@mui/icons-material/Science'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import CodeIcon from '@mui/icons-material/Code'
import BuildIcon from '@mui/icons-material/Build'
import LinkIcon from '@mui/icons-material/Link'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import SchoolIcon from '@mui/icons-material/School'
import RateReviewIcon from '@mui/icons-material/RateReview'
import TimelineIcon from '@mui/icons-material/Timeline'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import AddIcon from '@mui/icons-material/Add'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import AmbuNetLogo from '../assets/images/ambunetLogo.png'
import MainFrame from './MainFrame'
import Login from './Login'
import API from '../services/API'
import Loading from '../pages/loading/loadingPage'
import AmbuNetTheme from '../components/Theme'
import { CRM_REGIONS, crmUrlForRegion, currentRegion, initials, rootDomain } from './kit/helpers'
import { useDocumentTitle } from '../helpers/DocumentTitleManager'

export const CRMContext = createContext({})

export const SIDEBAR_WIDTH = 248

const NAV_SECTIONS = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', icon: <SpaceDashboardIcon />, to: '/', end: true },
    ],
  },
  {
    title: 'Sales',
    items: [
      { label: 'Pipeline', icon: <InsightsIcon />, to: '/sales' },
      { label: 'Forecast', icon: <TimelineIcon />, to: '/forecast' },
      { label: 'Campaigns', icon: <CampaignIcon />, to: '/campaigns' },
    ],
  },
  {
    title: 'Customers',
    items: [
      { label: 'Tenants', icon: <BusinessIcon />, to: '/tenants' },
      { label: 'Search Users', icon: <PersonSearchIcon />, to: '/users' },
      { label: 'Demo Users', icon: <PeopleAltIcon />, to: '/demo-users' },
      { label: 'Test Accounts', icon: <ScienceIcon />, to: '/test-accounts' },
    ],
  },
  {
    title: 'Clinical safety',
    items: [
      {
        label: 'Safety Incidents',
        icon: <HealthAndSafetyIcon />,
        to: '/safety-incidents',
      },
      {
        label: 'Clinical Hazard Log',
        icon: <GppMaybeIcon />,
        to: '/clinical-risk',
        end: true,
      },
      {
        label: 'Codebase Hazard Log',
        icon: <CodeIcon />,
        to: '/clinical-risk/codebase',
      },
    ],
  },
  {
    title: 'Training',
    items: [
      { label: 'Courses', icon: <SchoolIcon />, to: '/training/courses' },
      { label: 'Organisation links', icon: <LinkIcon />, to: '/training/links' },
      { label: 'Feedback', icon: <RateReviewIcon />, to: '/training/feedback' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { label: 'Maintenance', icon: <BuildIcon />, to: '/maintenance' },
      {
        label: 'Referral Codes',
        icon: <CardGiftcardIcon />,
        to: '/referral-codes',
      },
      {
        label: 'Docs',
        icon: <MenuBookIcon />,
        href: `https://docs.${rootDomain()}`,
      },
    ],
  },
]

const navStyles = (
  <GlobalStyles
    styles={{
      '.crm-nav': {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '9px 12px',
        margin: '1px 12px',
        borderRadius: 10,
        color: 'rgba(255,255,255,0.72)',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background-color 120ms ease, color 120ms ease',
      },
      '.crm-nav svg': { fontSize: 20, opacity: 0.85 },
      '.crm-nav:hover': {
        backgroundColor: 'rgba(255,255,255,0.08)',
        color: '#FFFFFF',
      },
      '.crm-nav.active': {
        backgroundColor: 'rgba(255,255,255,0.14)',
        color: '#FFFFFF',
        fontWeight: 600,
      },
      '.crm-nav.active svg': { opacity: 1 },
    }}
  />
)

// Each region runs its own CRM site with its own login; switching simply
// opens the same page on that region's crmadmin host.
const RegionSwitcher = () => {
  const [anchor, setAnchor] = useState(null)
  const region = currentRegion()
  return (
    <Box px={2} pb={0.5}>
      <Button
        fullWidth
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<UnfoldMoreIcon sx={{ opacity: 0.6 }} />}
        sx={{
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.5,
          borderRadius: 2.5,
          color: 'rgba(255,255,255,0.85)',
          bgcolor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
          '& .MuiButton-endIcon': { marginLeft: 'auto' },
        }}
      >
        <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
          {region.flag}
        </Box>
        {region.label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 232, borderRadius: 2.5 } }}
      >
        {CRM_REGIONS.map((option) => (
          <MenuItem
            key={option.key}
            selected={option.key === region.key}
            disabled={!option.live}
            onClick={() => {
              setAnchor(null)
              if (option.key !== region.key) window.location.assign(crmUrlForRegion(option))
            }}
          >
            <Box component="span" sx={{ fontSize: 18, mr: 1.5, lineHeight: 1 }}>
              {option.flag}
            </Box>
            <ListItemText
              primary={option.label}
              secondary={option.live ? `crmadmin.${option.domain}` : 'Coming soon'}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

const SidebarContent = ({ user, onNavigate, onLogout }) => {
  const navigate = useNavigate()
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return (
    <Stack height="100%" minHeight={0}>
      <Stack direction="row" alignItems="center" gap={1} px={2.5} py={2}>
        <Box
          component="img"
          src={AmbuNetLogo}
          alt=""
          sx={{ height: 34, width: 34, objectFit: 'contain' }}
        />
        <Box>
          <Typography
            sx={{
              fontFamily: 'moonlander, Poppins, sans-serif',
              color: '#FFF',
              lineHeight: 1,
            }}
          >
            AmbuNet
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 700,
              fontSize: 10,
            }}
          >
            Admin CRM
          </Typography>
        </Box>
      </Stack>

      <RegionSwitcher />

      <Box px={2} pb={1.5}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            onNavigate?.()
            navigate('/sales/new')
          }}
          sx={{
            bgcolor: '#239AA4',
            '&:hover': { bgcolor: '#1E7F88' },
            borderRadius: 2.5,
            boxShadow: 'none',
          }}
        >
          New lead
        </Button>
      </Box>

      <Box flex={1} overflow="auto" pb={1}>
        {NAV_SECTIONS.map((section) => (
          <Box key={section.title} mt={1}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 3,
                pb: 0.5,
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontSize: 10,
              }}
            >
              {section.title}
            </Typography>
            {section.items.map((item) =>
              item.href ? (
                <a
                  key={item.label}
                  className="crm-nav"
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.icon}
                  <Box flex={1}>{item.label}</Box>
                  <OpenInNewIcon
                    sx={{ fontSize: '14px !important', opacity: 0.5 }}
                  />
                </a>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    isActive ? 'crm-nav active' : 'crm-nav'
                  }
                  onClick={onNavigate}
                >
                  {item.icon}
                  <Box flex={1}>{item.label}</Box>
                </NavLink>
              ),
            )}
          </Box>
        ))}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Avatar
          src={user.photograph}
          sx={{
            width: 34,
            height: 34,
            bgcolor: '#239AA4',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {initials(fullName || user.username || 'A')}
        </Avatar>
        <Box minWidth={0} flex={1}>
          <Typography
            variant="body2"
            sx={{ color: '#FFF', fontWeight: 600 }}
            noWrap
          >
            {fullName || user.username || 'Admin'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.55)' }}
            noWrap
          >
            {user.email || 'Signed in'}
          </Typography>
        </Box>
        <Tooltip title="Log out">
          <IconButton
            size="small"
            onClick={onLogout}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )
}

const sidebarSx = {
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  background: 'linear-gradient(180deg, #123F4A 0%, #0F2F36 100%)',
  color: '#FFF',
  boxSizing: 'border-box',
}

const CRM = () => {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState({})
  const [drawerOpen, setDrawerOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()

  useEffect(() => {
    const subdomain = window.location.hostname.split('.')[0]
    if (subdomain === 'crmadmin') {
      document.title = 'CRM Admin'
      return
    }

    document.title = 'CRM AmbuNet'
  }, [location])

  useEffect(() => {
    API.get('/admincrm/account/me')
      .then((res) => setUser(res.status === 200 ? res.data : {}))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const logout = () =>
    API.post('/admincrm/account/logout').then(() => window.location.reload())

  if (loading) return <Loading />

  if (!user || Object.keys(user).length === 0) {
    return <Login setUser={setUser} />
  }

  return (
    <AmbuNetTheme>
      {navStyles}
      <CRMContext.Provider value={{ user, setUser }}>
        <Box display="flex" minHeight="100vh" width="100%" bgcolor="#F1F5F9">
          {isMobile ? (
            <>
              <AppBar
                position="fixed"
                elevation={0}
                sx={{
                  background:
                    'linear-gradient(90deg, #123F4A 0%, #0F2F36 100%)',
                }}
              >
                <Toolbar variant="dense" sx={{ gap: 1 }}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="Open navigation"
                    onClick={() => setDrawerOpen(true)}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Typography
                    sx={{ fontFamily: 'moonlander, Poppins, sans-serif' }}
                  >
                    AmbuNet
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Admin CRM
                  </Typography>
                </Toolbar>
              </AppBar>
              <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: sidebarSx }}
              >
                <SidebarContent
                  user={user}
                  onNavigate={() => setDrawerOpen(false)}
                  onLogout={logout}
                />
              </Drawer>
            </>
          ) : (
            <Box
              component="aside"
              sx={{
                ...sidebarSx,
                position: 'sticky',
                top: 0,
                height: '100vh',
              }}
            >
              <SidebarContent user={user} onLogout={logout} />
            </Box>
          )}
          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              pt: isMobile ? 7 : 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MainFrame />
          </Box>
        </Box>
      </CRMContext.Provider>
    </AmbuNetTheme>
  )
}

export default CRM
